import React, {useEffect, useState, useCallback} from 'react';
import {Box, Typography, CircularProgress, Alert} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from '@mui/lab';
import {getRecentActivities} from 'src/apis/autoGenerate/services';
import {
  IntercomDeviceLogRecentActivity,
  IntercomDeviceLogRecentActivityItem,
} from 'src/apis/autoGenerate/types/intercomDeviceLog';
import {DoorAccessIcon, IntercomCallIcon, ActivityIcon} from 'src/components/Icons/activity';
import {useAppSelector} from 'src/redux/hooks';
import {selectDevice} from 'src/redux/slice/deviceSlice';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {EntioCard} from 'src/components/ui';

dayjs.extend(utc);
dayjs.extend(timezone);

interface RecentActivitiesCardProps {
  deviceIds?: string[];
  maxActivities?: number;
}

interface ActivityWithDevice extends IntercomDeviceLogRecentActivity {
  deviceId?: string;
}

export default function RecentActivitiesCard({
  deviceIds = [],
  maxActivities = 5,
}: RecentActivitiesCardProps) {
  const [activities, setActivities] = useState<ActivityWithDevice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deviceState = useAppSelector(selectDevice);

  useEffect(() => {
    const fetchRecentActivities = async () => {
      if (deviceIds.length === 0) {
        setActivities([]);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await getRecentActivities(deviceIds.join(','));

        const allActivities = response.data.data
          .flatMap((item: IntercomDeviceLogRecentActivityItem) =>
            item.activities.map((activity: IntercomDeviceLogRecentActivity) => ({
              ...activity,
              deviceId: item.deviceId,
            }))
          )
          .sort(
            (a: ActivityWithDevice, b: ActivityWithDevice) =>
              new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
          )
          .slice(0, maxActivities);

        setActivities(allActivities);
      } catch (err) {
        console.error('Error fetching recent activities:', err);
        setError('Failed to load recent activities');
      } finally {
        setLoading(false);
      }
    };

    fetchRecentActivities();
  }, [deviceIds, maxActivities]);

  const formatTimeWithTimezone = useCallback(
    (timestamp: string, deviceId?: string) => {
      if (!timestamp) {
        return {date: '-', time: '-', timezone: '', fullTimeZone: ''};
      }

      let timeZone = 'UTC';
      if (deviceId) {
        const device = deviceState.deviceList.find(d => d.id === deviceId);
        timeZone = device?.timeZone || 'UTC';
      }

      try {
        const localTime = dayjs.utc(timestamp).tz(timeZone);
        const tzAbbr = localTime.format('z');
        return {
          date: localTime.format('MMM DD, YYYY'),
          time: localTime.format('hh:mm A'),
          timezone: tzAbbr !== 'z' ? tzAbbr : '',
          fullTimeZone: timeZone,
        };
      } catch (error) {
        const fallbackTime = dayjs.utc(timestamp);
        return {
          date: fallbackTime.format('MMM DD, YYYY'),
          time: fallbackTime.format('hh:mm A'),
          timezone: 'UTC',
          fullTimeZone: 'UTC',
        };
      }
    },
    [deviceState.deviceList]
  );

  const getActivityTypeDisplay = (type: string) => {
    switch (type) {
      case 'DOOR_ACCESS':
        return 'Door Access';
      case 'INTERCOM_CALL':
        return 'Intercom Call';
      default:
        return type.replace(/_/g, ' ');
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'DOOR_ACCESS':
        return <DoorAccessIcon sx={{fontSize: '16px'}} />;
      case 'INTERCOM_CALL':
        return <IntercomCallIcon sx={{fontSize: '16px'}} />;
      default:
        return <ActivityIcon sx={{fontSize: '16px'}} />;
    }
  };

  if (loading) {
    return (
      <EntioCard title="Recent Activities">
        <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4}}>
          <CircularProgress size={24} />
        </Box>
      </EntioCard>
    );
  }

  return (
    <EntioCard title="Recent Activities">
      {error ? (
        <Alert severity="error" sx={{mb: 2}}>
          {error}
        </Alert>
      ) : activities.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{textAlign: 'center', py: 2}}>
          No recent activities
        </Typography>
      ) : (
        <Timeline
          sx={{
            p: 0,
            m: 0,
            '& .MuiTimelineItem-root:before': {
              flex: 0,
              padding: 0,
            },
          }}
        >
          {activities.map((activity, index) => {
            const timeInfo = formatTimeWithTimezone(activity.timestamp, activity.deviceId);

            return (
              <TimelineItem key={activity.id}>
                <TimelineSeparator>
                  <TimelineDot
                    color="primary"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 32,
                      height: 32,
                      backgroundColor: 'primary.main',
                      color: 'white',
                      '& .MuiSvgIcon-root': {
                        fontSize: '16px',
                      },
                    }}
                  >
                    {getActivityIcon(activity.type)}
                  </TimelineDot>
                  {index !== activities.length - 1 && <TimelineConnector />}
                </TimelineSeparator>
                <TimelineContent>
                  <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap'}}>
                    <Typography variant="subtitle2" component="span">
                      {timeInfo.date}
                    </Typography>
                    <Typography variant="subtitle2" component="span">
                      · {timeInfo.time}
                    </Typography>
                    {timeInfo.timezone && (
                      <Typography variant="subtitle2" component="span">
                        · {timeInfo.timezone}
                      </Typography>
                    )}
                    <Typography variant="caption" component="span" sx={{color: 'text.secondary'}}>
                      ({timeInfo.fullTimeZone})
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{fontWeight: 500}}>
                    {getActivityTypeDisplay(activity.type)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {activity.details.location || activity.details.person} •{' '}
                    {activity.user?.name || 'Unknown User'}
                  </Typography>
                  {activity.description && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {activity.description}
                    </Typography>
                  )}
                </TimelineContent>
              </TimelineItem>
            );
          })}
        </Timeline>
      )}
    </EntioCard>
  );
}
